/**
 * /me/character: a developer builds the character that stands for them in the room and the Bug
 * Hunt. Their own, and nobody else's: the server lets a person set only their own avatar unless
 * they are an admin.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { EmptyState, Panel, Skeleton } from '../components/primitives'
import { CharacterBuilder } from '../components/CharacterBuilder'
import { StatusPhrase } from '../components/StatusPhrase'
import { AgentLooksPicker } from '../components/AgentLooksPicker'
import type { Avatar } from '../lib/types'

export default function MyCharacter() {
  const { t } = useI18n()
  const [saved, setSaved] = useState(false)
  return (
    <Panel title={t('character.title')} subtitle={t('character.hint')} actions={saved ? <span className="small" style={{ color: 'var(--ok-text)' }}>{t('character.saved')}</span> : undefined}>
      <MyCharacterEditor onSaved={() => {
        setSaved(true)
        window.setTimeout(() => setSaved(false), 4000)
      }} />
    </Panel>
  )
}

/**
 * The editor itself: the line under the name and the character. The page above, and the room's right-click
 * ("Mudar meu personagem") in a window, without leaving the room.
 */
export function MyCharacterEditor({ onSaved, onCancel }: { onSaved?: () => void; onCancel?: () => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const developerId = me?.developerId ?? null

  const { data: avatar, isLoading: loadingAvatar } = useQuery({
    queryKey: ['avatar', developerId],
    queryFn: () => api.settings.getAvatar(developerId!),
    enabled: developerId != null,
  })

  const save = useMutation({
    mutationFn: (next: Avatar) => api.settings.setAvatar(developerId!, next),
    onSuccess: () => {
      onSaved?.()
      for (const key of ['avatar', 'bug-hunt', 'room-activity', 'developers']) void queryClient.invalidateQueries({ queryKey: [key] })
    },
  })

  if (isLoading || (developerId && loadingAvatar)) return <Skeleton height={320} />
  if (!developerId) {
    return (
      <Panel>
        <EmptyState title={t('character.noDeveloper')} body={t('character.noDeveloperHint')} />
      </Panel>
    )
  }

  return (
    <div className="stack">
      {/* The line under the name is theirs to write, like everything else on this page. */}
      <StatusPhrase />
      <CharacterBuilder key={developerId} developerId={developerId} initial={avatar ?? null} busy={save.isPending} onSave={(next) => save.mutate(next)} onCancel={onCancel} />
      {/* Your agents' looks, next to yours (docs/refactor/25): one place for how you appear in the office. */}
      <AgentLooksPicker />
    </div>
  )
}
